<template>
	<article class="detail-content" v-if="data">
		<!-- 视频播放器 -->
		<figure class="detail-content__video" v-if="data.media_url && isMp4Video(data.media_url)">
			<video
				v-if="showVideoPlayer && playvideo(data.media_url)"
				ref="videoRef"
				controls
				width="100%"
				:src="getVideoUrl(data.media_url)"
				@click.stop
				autoplay
				@play="onVideoPlay"
				@timeupdate="onVideoTimeUpdate"
				:aria-label="t('square.detail.video.playAria', { title: data.title })"
			></video>
			<button
				v-else
				type="button"
				@click.prevent="onPlayPreviewClick"
				class="detail-content__video-preview"
				:aria-label="t('square.detail.video.playAria', { title: data.title })"
			>
				<img
					:src="data.cover"
					:alt="t('square.detail.video.coverAlt', { title: data.title })"
					class="detail-content__cover"
				/>
				<div class="detail-content__mask" v-if="data.type == 1 && playvideo(data.media_url)"></div>
				<img
					:src="videoIcon"
					:alt="t('square.detail.video.playButtonAlt')"
					class="detail-content__play-icon"
					v-if="data.type == 1 && playvideo(data.media_url)"
				/>
			</button>
		</figure>
		<figure
			class="detail-content__video"
			v-else-if="data.media_url && isYoutubeUrl(data.media_url)"
		>
			<div v-if="!showYoutubePlayer">
				<button
					type="button"
					@click.prevent="onYoutubePlayClick"
					class="detail-content__video-preview"
					:aria-label="t('square.detail.video.youtubePlayAria', { title: data.title })"
				>
					<img
						:src="data.cover"
						:alt="t('square.detail.video.coverAlt', { title: data.title })"
						class="detail-content__cover"
					/>
					<div class="detail-content__mask" v-if="data.type == 1"></div>
					<img
						:src="videoIcon"
						:alt="t('square.detail.video.playButtonAlt')"
						class="detail-content__play-icon"
						v-if="data.type == 1"
					/>
				</button>
			</div>
			<div v-else class="detail-content__youtube">
				<iframe
					width="100%"
					height="450"
					:src="getYoutubeEmbedUrl(data.media_url)"
					frameborder="0"
					allow="
						accelerometer;
						autoplay;
						clipboard-write;
						encrypted-media;
						gyroscope;
						picture-in-picture;
					"
					allowfullscreen
					:title="t('square.detail.video.youtubeTitle', { title: data.title })"
				></iframe>
			</div>
		</figure>

		<!-- 图文封面 -->
		<figure class="detail-content__cover" v-else-if="isArticleType && articleCover">
			<img
				:src="articleCover"
				:alt="t('square.detail.article.coverAlt', { title: data.title })"
				class="detail-content__cover-img"
			/>
		</figure>

		<!-- 标题（视频类显示） -->
		<h1 v-if="!isArticleType" class="detail-content__title">{{ data.title }}</h1>

		<!-- 图文正文 -->
		<div
			v-if="isArticleType && articleContent"
			ref="articleBodyRef"
			class="detail-content__article-body"
			v-html="articleContent"
		></div>

		<!-- 播放量 & 时间 -->
		<div class="detail-content__meta">
			<img :src="metaIcon" alt="" class="detail-content__meta-icon" />
			<span>{{ data.view_count || data.media_play_count || 0 }}</span>
			<span class="detail-content__meta-divider">|</span>
			<span>{{ data.format_time }}</span>
		</div>

		<!-- 作者 & 操作栏 -->
		<div class="detail-content__author-bar">
			<div class="detail-content__author">
				<img
					:src="publisherInfo.avatar"
					:alt="publisherInfo.nickname"
					class="detail-content__avatar"
				/>
				<span class="detail-content__nickname">{{ publisherInfo.nickname }}</span>
				<button
					type="button"
					class="detail-content__follow-btn"
					:class="{ 'detail-content__follow-btn--followed': isFollowed }"
					@click="handleFollow"
				>
					{{ isFollowed ? t("square.detail.followed") : t("square.detail.follow") }}
				</button>
			</div>
			<div class="detail-content__actions">
				<button
					type="button"
					class="detail-content__action-btn"
					:disabled="squareZanLoading"
					@click="handleZan"
				>
					<img :src="isZaned ? zanAfterIcon : zanBeforeIcon" alt="" />
					<span>{{ t("square.detail.like") }}</span>
				</button>
				<button type="button" class="detail-content__action-btn" @click="showSharePanel = true">
					<img :src="shareIcon" alt="" />
					<span>{{ t("square.detail.share") }}</span>
				</button>
			</div>
		</div>

		<SharePanel v-model:show="showSharePanel" :share-list="shareList" @share="handleShare" />

		<!-- 评论区 -->
		<section class="detail-content__comments">
			<h2 class="detail-content__comments-title">
				{{ t("square.detail.comments") }} <span>{{ commentTotal }}</span>
			</h2>
			<div class="detail-content__comment-input">
				<input
					v-model="mainCommentText"
					type="text"
					:placeholder="t('square.detail.commentPlaceholder')"
					maxlength="500"
					:disabled="commentSubmitting"
					@focus="onCommentInputFocus"
					@keyup.enter="submitMainComment"
				/>
				<button
					type="button"
					class="detail-content__comment-send"
					:disabled="commentSubmitting || !mainCommentText.trim()"
					@click="submitMainComment"
				>
					{{ commentSubmitting ? t("square.detail.sending") : t("square.detail.send") }}
				</button>
			</div>
			<ul v-if="commentList.length" class="detail-content__comment-list">
				<li v-for="item in commentList" :key="item.id" class="detail-content__comment-item">
					<img
						:src="item.user_info?.avatar"
						:alt="item.user_info?.nickname"
						class="detail-content__comment-avatar"
					/>
					<div class="detail-content__comment-body">
						<div class="detail-content__comment-header">
							<span class="detail-content__comment-user">
								@{{ item.user_info?.nickname }}·{{ item.format_time }}
							</span>
						</div>
						<p class="detail-content__comment-text">{{ item.reply }}</p>
						<div class="detail-content__comment-footer">
							<button
								type="button"
								class="detail-content__comment-zan"
								:disabled="isCommentZanLoading(item.id)"
								@click.stop="handleCommentZan(item)"
							>
								<img :src="item.is_zan ? zanAfterIcon : zanBeforeIcon" alt="" />
								<span>{{ item.zan || 0 }}</span>
							</button>
							<button
								type="button"
								class="detail-content__comment-reply"
								@click.stop="toggleReplyInput(item)"
							>
								<img :src="pinlunIcon" alt="" />
							</button>
						</div>
						<div
							v-if="String(activeReplyId) === String(item.id)"
							class="detail-content__reply-input"
						>
							<input
								v-model="activeReplyDraft"
								type="text"
								:placeholder="
									t('square.detail.replyPlaceholder', {
										nickname: item.user_info?.nickname || '',
									})
								"
								maxlength="500"
								:disabled="replySubmitting"
								@keyup.enter="submitReplyComment(item)"
							/>
							<button
								type="button"
								class="detail-content__comment-send detail-content__comment-send--inline"
								:disabled="replySubmitting || !activeReplyDraft.trim()"
								@click="submitReplyComment(item)"
							>
								{{ replySubmitting ? t("square.detail.sending") : t("square.detail.send") }}
							</button>
						</div>
						<button
							v-if="item.reply_count > 0"
							type="button"
							class="detail-content__comment-view-replies"
							:disabled="getReplyState(item.id).loading"
							@click="toggleViewReplies(item)"
						>
							<template v-if="getReplyState(item.id).loading">{{
								t("square.detail.loading")
							}}</template>
							<template v-else-if="getReplyState(item.id).expanded">{{
								t("square.detail.collapseReplies")
							}}</template>
							<template v-else
								>{{ t("square.detail.viewReplies", { count: item.reply_count }) }} &gt;</template
							>
						</button>
						<ul
							v-if="getReplyState(item.id).expanded && getReplyState(item.id).list.length"
							class="detail-content__reply-list"
						>
							<li
								v-for="reply in getReplyState(item.id).list"
								:key="reply.id"
								class="detail-content__reply-item"
							>
								<img
									:src="reply.user_info?.avatar"
									:alt="reply.user_info?.nickname"
									class="detail-content__reply-avatar"
								/>
								<div class="detail-content__reply-body">
									<div class="detail-content__comment-header">
										<span class="detail-content__comment-user">
											@{{ reply.user_info?.nickname }}·{{ reply.format_time }}
										</span>
									</div>
									<p class="detail-content__comment-text">{{ reply.reply }}</p>
									<div class="detail-content__comment-footer">
										<button
											type="button"
											class="detail-content__comment-zan"
											:disabled="isCommentZanLoading(reply.id)"
											@click.stop="handleCommentZan(reply)"
										>
											<img :src="reply.is_zan ? zanAfterIcon : zanBeforeIcon" alt="" />
											<span>{{ reply.zan || 0 }}</span>
										</button>
									</div>
								</div>
							</li>
							<li v-if="hasMoreReplies(item.id)" class="detail-content__reply-load-more-wrap">
								<button
									type="button"
									class="detail-content__comment-view-replies"
									:disabled="getReplyState(item.id).loading"
									@click="loadMoreReplies(item)"
								>
									{{
										getReplyState(item.id).loading
											? t("square.detail.loading")
											: t("square.detail.loadMoreReplies")
									}}
								</button>
							</li>
						</ul>
					</div>
				</li>
			</ul>
			<p v-else-if="!commentLoading" class="detail-content__comment-empty">
				{{ t("square.detail.emptyComments") }}
			</p>
			<button
				v-if="hasMoreComments"
				type="button"
				class="detail-content__comment-load-more"
				:disabled="commentLoading"
				@click="loadMoreComments"
			>
				{{ commentLoading ? t("square.detail.loading") : t("square.detail.loadMoreComments") }}
			</button>
		</section>
	</article>
</template>

<script setup>
import { computed, inject, onBeforeUnmount, onMounted, ref, watch } from "vue";
import { useI18n } from "~/composables/useI18n";
import { useSquare } from "~/composables/useSquare";
import SharePanel from "../share-panel/index.vue";
import {
	buildDetailContentParams,
	resolveDetailMediaType,
	useArticleReadProgress,
	usePlayProgressTracking,
} from "../../../composables/useSquareDetailTracking";
import videoIcon from "../../../images/vedio.png";
import videoIconXianx from "../../../images/vedio-xx.png";
import eyeIcon from "../../../../square/images/eye.png";
import shareIcon from "../../../images/sahre.png";
import zanAfterIcon from "../../../images/zan-after.png";
import zanBeforeIcon from "../../../images/zan-before.png";
import pinlunIcon from "../../../images/pinlun.png";

const props = defineProps({
	data: {
		type: Object,
		default: () => ({}),
	},
});

const isArticleType = computed(() => Number(props.data?.type) === 2);

const { t, locale } = useI18n();
const logDetailEvent = inject("logDetailEvent", null);

const videoRef = ref(null);
const articleBodyRef = ref(null);

const { handleScroll: handleArticleScroll } = useArticleReadProgress(
	logDetailEvent,
	() => props.data,
	() => (import.meta.client ? document.documentElement : null)
);

const {
	trackPlayClick,
	handleTimeUpdate,
	markWatchStart,
	reset: resetPlayProgress,
} = usePlayProgressTracking(logDetailEvent, () => props.data);

let removeArticleScrollListener = null;

onMounted(() => {
	if (!import.meta.client || !isArticleType.value) return;
	const onScroll = () => handleArticleScroll();
	window.addEventListener("scroll", onScroll, { passive: true });
	removeArticleScrollListener = () => window.removeEventListener("scroll", onScroll);
});

onBeforeUnmount(() => {
	removeArticleScrollListener?.();
});

watch(
	() => props.data?.id,
	() => {
		resetPlayProgress();
	}
);

const onPlayPreviewClick = () => {
	trackPlayClick({ playPositionBucket: "main" });
	showVideoPlayer.value = true;
};

const onYoutubePlayClick = () => {
	trackPlayClick({ playPositionBucket: "main" });
	showYoutubePlayer.value = true;
};

const onVideoPlay = () => {
	markWatchStart();
};

const onVideoTimeUpdate = () => {
	handleTimeUpdate(videoRef.value);
};

const onCommentInputFocus = () => {
	if (!logDetailEvent) return;
	logDetailEvent(
		"media_comment_input_focus",
		buildDetailContentParams(props.data, {
			media_type: resolveDetailMediaType(props.data),
		})
	);
};

const {
	isFollowed,
	isZaned,
	squareZanLoading,
	shareList,
	showSharePanel,
	handleFollow,
	handleZan,
	handleShare,
	commentList,
	commentLoading,
	commentTotal,
	hasMoreComments,
	mainCommentText,
	activeReplyId,
	activeReplyDraft,
	commentSubmitting,
	replySubmitting,
	getReplyState,
	hasMoreReplies,
	toggleViewReplies,
	loadMoreReplies,
	loadMoreComments,
	toggleReplyInput,
	submitMainComment,
	submitReplyComment,
	handleCommentZan,
	isCommentZanLoading,
} = useSquare(
	() => props.data,
	() => null
);

const publisherInfo = computed(() => {
	return (
		props.data?.publisher_info || {
			nickname: "",
			avatar: "",
		}
	);
});

const metaIcon = computed(() => (isArticleType.value ? eyeIcon : videoIconXianx));

const mediaImages = computed(() => {
	const mediaUrl = props.data?.media_url;
	if (!mediaUrl) return [];
	if (Array.isArray(mediaUrl)) {
		return mediaUrl.filter(item => typeof item === "string" && item.trim());
	}
	if (typeof mediaUrl === "string") {
		try {
			const parsed = JSON.parse(mediaUrl);
			if (Array.isArray(parsed)) {
				return parsed
					.map(item =>
						String(item || "")
							.replace(/\\\//g, "/")
							.trim()
					)
					.filter(Boolean);
			}
		} catch {
			return mediaUrl
				.split(",")
				.map(item =>
					item
						.replace(/[\[\]"]/g, "")
						.replace(/\\\//g, "/")
						.trim()
				)
				.filter(Boolean);
		}
	}
	return [];
});

const articleCover = computed(() => {
	return props.data?.cover || mediaImages.value[0] || "";
});

const articleContent = computed(() => {
	if (!props.data) return "";
	if (locale.value === "en") {
		return props.data.content_en || props.data.content || "";
	}
	return props.data.content || props.data.content_en || "";
});

const showVideoPlayer = ref(false);
const showYoutubePlayer = ref(false);

const getVideoUrl = media_url => {
	if (!media_url) return "";
	if (Array.isArray(media_url) && media_url.length > 0) {
		return String(media_url[0]).replace(/\\\//g, "/");
	}
	if (typeof media_url !== "string") return "";
	let url = media_url;
	try {
		const parsed = JSON.parse(media_url);
		if (Array.isArray(parsed) && parsed.length > 0 && parsed[0]) {
			url = String(parsed[0]);
		}
	} catch {
		url = media_url.replace('["', "").replace('"]', "");
	}
	return url.replace(/\\\//g, "/");
};

const playvideo = media_url => getVideoUrl(media_url) !== "";

const isMp4Video = media_url => {
	const url = getVideoUrl(media_url);
	return url.toLowerCase().endsWith(".mp4");
};

const isYoutubeUrl = url => {
	const videoUrl = getVideoUrl(url);
	return videoUrl.includes("youtube.com") || videoUrl.includes("youtu.be");
};

const getYoutubeEmbedUrl = url => {
	const videoUrl = getVideoUrl(url);
	let videoId = "";
	if (videoUrl.includes("youtube.com/watch?v=")) {
		videoId = videoUrl.split("v=")[1];
		const ampersandPosition = videoId.indexOf("&");
		if (ampersandPosition !== -1) {
			videoId = videoId.substring(0, ampersandPosition);
		}
	} else if (videoUrl.includes("youtu.be/")) {
		videoId = videoUrl.split("youtu.be/")[1];
	}
	return `https://www.youtube.com/embed/${videoId}?autoplay=1&rel=0`;
};
</script>

<style lang="scss" scoped>
@mixin line-clamp($lines: 2) {
	display: -webkit-box;
	-webkit-line-clamp: $lines;
	-webkit-box-orient: vertical;
	overflow: hidden;
	text-overflow: ellipsis;
}

.detail-content {
	width: 100%;

	&__video {
		margin: 0 0 20px;
		position: relative;
		width: 100%;
		aspect-ratio: 16 / 9;
		border-radius: 10px;
		overflow: hidden;
		line-height: 0;
		background: #000;

		video {
			width: 100%;
			height: 100%;
			display: block;
			object-fit: contain;
		}
	}

	&__video-preview {
		border: none;
		background: transparent;
		padding: 0;
		cursor: pointer;
		position: relative;
		display: block;
		width: 100%;

		.detail-content__cover {
			width: 100%;
			height: auto;
			display: block;
			aspect-ratio: 16 / 9;
			object-fit: cover;
		}
	}

	&__play-icon {
		position: absolute;
		width: 56px;
		height: auto;
		top: 50%;
		left: 50%;
		transform: translate(-50%, -50%);
		pointer-events: none;
	}

	&__mask {
		position: absolute;
		inset: 0;
		background: rgba(0, 0, 0, 0.15);
		pointer-events: none;
	}

	&__youtube {
		position: relative;
		width: 100%;
		aspect-ratio: 16 / 9;

		iframe {
			width: 100%;
			height: 100%;
		}
	}

	&__cover {
		margin: 0 0 20px;
		border-radius: 12px;
		overflow: hidden;
		background: #f5f5f5;
	}

	&__cover-img {
		display: block;
		width: 100%;
		height: auto;
	}

	&__article-body {
		margin-bottom: 20px;
		font-size: 16px;
		color: #333333;
		line-height: 1.8;
		word-break: break-word;

		:deep(p) {
			margin: 0 0 16px;

			&:last-child {
				margin-bottom: 0;
			}
		}

		:deep(img) {
			max-width: 100%;
			height: auto;
			border-radius: 8px;
		}
	}

	&__title {
		margin: 0 0 12px;
		font-size: 22px;
		font-weight: 500;
		color: #333;
		line-height: 1.5;
		@include line-clamp(3);
	}

	&__meta {
		display: flex;
		align-items: center;
		gap: 6px;
		margin-bottom: 20px;
		font-size: 14px;
		color: #999999;
	}

	&__meta-icon {
		width: 16px;
		height: 16px;
		object-fit: contain;
		opacity: 0.6;
	}

	&__meta-divider {
		color: #cccccc;
	}

	&__author-bar {
		display: flex;
		align-items: center;
		justify-content: space-between;
		padding-bottom: 24px;
		margin-bottom: 24px;
	}

	&__author {
		display: flex;
		align-items: center;
		gap: 10px;
		min-width: 0;
	}

	&__avatar {
		width: 52px;
		height: 52px;
		border-radius: 50%;
		object-fit: cover;
		flex-shrink: 0;
	}

	&__nickname {
		font-size: 22px;
		font-weight: 500;
		color: #333333;
		white-space: nowrap;
		overflow: hidden;
		text-overflow: ellipsis;
		max-width: 200px;
	}

	&__follow-btn {
		flex-shrink: 0;
		padding: 6px 30px;
		border: none;
		border-radius: 8px;
		background: #f2af19;
		color: #ffffff;
		font-size: 20px;
		font-weight: 500;
		cursor: pointer;
		white-space: nowrap;
		margin-left: 20px;

		&--followed {
			background: #f0f0f0;
			color: #999999;
		}

		&:hover {
			opacity: 0.7;
		}
	}

	&__actions {
		display: flex;
		align-items: center;
		gap: 12px;
		flex-shrink: 0;
	}

	&__action-btn {
		display: flex;
		align-items: center;
		justify-content: center;
		gap: 4px;
		border: none;
		border-radius: 4px;
		background: #f6f6f6;
		cursor: pointer;
		padding: 7px 15px;

		&:disabled {
			cursor: not-allowed;
			opacity: 0.6;
		}

		img {
			width: 22px;
			height: 22px;
			object-fit: contain;
		}

		span {
			font-size: 16px;
			color: #666666;
		}
	}

	&__comments {
		border: 1px solid #efefef;
		padding: 20px;
		border-radius: 12px;
	}

	&__comments-title {
		margin: 0 0 16px;
		font-size: 22px;
		font-weight: 700;
		color: #333333;

		span {
			color: #666666;
		}
	}

	&__comment-input {
		display: flex;
		align-items: center;
		gap: 12px;
		margin-bottom: 24px;

		input {
			flex: 1;
			min-width: 0;
			height: 44px;
			padding: 0 16px;
			border: none;
			border-radius: 22px;
			background: #f5f5f5;
			font-size: 16px;
			color: #333333;
			outline: none;
			box-sizing: border-box;

			&::placeholder {
				color: #bbbbbb;
			}

			&:disabled {
				cursor: not-allowed;
			}
		}
	}

	&__comment-send {
		flex-shrink: 0;
		height: 36px;
		padding: 0 16px;
		border: none;
		border-radius: 18px;
		background: #f2af19;
		color: #ffffff;
		font-size: 16px;
		cursor: pointer;
		white-space: nowrap;

		&:disabled {
			cursor: not-allowed;
			opacity: 0.6;
		}

		&--inline {
			height: 32px;
			padding: 0 14px;
			font-size: 12px;
		}
	}

	&__reply-input {
		display: flex;
		align-items: center;
		gap: 10px;
		margin-top: 12px;

		input {
			flex: 1;
			min-width: 0;
			height: 36px;
			padding: 0 14px;
			border: none;
			border-radius: 18px;
			background: #f5f5f5;
			font-size: 13px;
			color: #333333;
			outline: none;
			box-sizing: border-box;

			&::placeholder {
				color: #bbbbbb;
			}

			&:disabled {
				cursor: not-allowed;
			}
		}
	}

	&__comment-list {
		list-style: none;
		margin: 0;
		padding: 0;
	}

	&__comment-item {
		display: flex;
		gap: 12px;
		margin-bottom: 24px;
		position: relative;

		&:last-child {
			margin-bottom: 0;
		}
	}

	&__comment-avatar {
		width: 36px;
		height: 36px;
		border-radius: 50%;
		object-fit: cover;
		flex-shrink: 0;
		background: #eeeeee;
	}

	&__comment-body {
		flex: 1;
		min-width: 0;
		padding-right: 24px;
	}

	&__comment-header {
		display: flex;
		align-items: center;
		gap: 8px;
		margin-bottom: 6px;
	}

	&__comment-user {
		font-size: 14px;
		color: #585858;
	}

	&__comment-text {
		margin: 0 0 10px;
		font-size: 14px;
		color: #333333;
		line-height: 1.6;
	}

	&__comment-footer {
		display: flex;
		align-items: center;
		gap: 16px;
	}

	&__comment-zan,
	&__comment-reply {
		display: flex;
		align-items: center;
		gap: 4px;
		border: none;
		background: transparent;
		padding: 0;
		cursor: pointer;
		font-size: 12px;
		color: #333333;

		&:disabled {
			cursor: not-allowed;
			opacity: 0.6;
		}

		img {
			width: 16px;
			height: 16px;
			object-fit: contain;
		}
	}

	&__comment-view-replies {
		border: none;
		background: transparent;
		padding: 0;
		font-size: 12px;
		color: #999999;
		cursor: pointer;
		margin-top: 10px;

		&:disabled {
			cursor: not-allowed;
			color: #bbbbbb;
		}
	}

	&__reply-list {
		list-style: none;
		margin: 12px 0 0;
		padding: 0 0 0 12px;
		border-left: 2px solid #f0f0f0;
	}

	&__reply-item {
		display: flex;
		gap: 10px;
		margin-bottom: 16px;

		&:last-child {
			margin-bottom: 0;
		}
	}

	&__reply-avatar {
		width: 28px;
		height: 28px;
		border-radius: 50%;
		object-fit: cover;
		flex-shrink: 0;
		background: #eeeeee;
	}

	&__reply-body {
		flex: 1;
		min-width: 0;
	}

	&__reply-load-more-wrap {
		list-style: none;
		margin-top: 8px;
	}

	&__comment-empty {
		margin: 0;
		padding: 24px 0;
		text-align: center;
		font-size: 14px;
		color: #999999;
	}

	&__comment-load-more {
		display: block;
		width: 100%;
		margin-top: 16px;
		padding: 10px 0;
		border: none;
		background: transparent;
		font-size: 14px;
		color: #666666;
		cursor: pointer;

		&:hover:not(:disabled) {
			color: #b9703a;
		}

		&:disabled {
			cursor: not-allowed;
			color: #bbbbbb;
		}
	}

	&__comment-more {
		position: absolute;
		top: 0;
		right: 0;
		border: none;
		background: transparent;
		padding: 4px;
		cursor: pointer;

		img {
			width: 16px;
			height: 16px;
			object-fit: contain;
		}
	}
}
</style>
